// General purpose entity action stuff, good for any entity type

import { EventEmitter } from "@angular/core";
import { CHDataActionPayload, CHSuccessActionResult, SnackbarActions } from "@app/root-store/actions";
import { FailureAction, SuccessAction } from "@app/root-store/effects/effect-creators";
import { DbObject } from "@app/shared/model/db-object.model";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { Action } from "@ngrx/store";
import { TypedAction } from "@ngrx/store/src/models";
import { Observable, UnaryFunction, of, pipe } from "rxjs";
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from "rxjs/operators";
import { DataServiceError } from "../services/data-service-error";
import { ActionWord } from "./action-word";

export abstract class CHDataAction<T> implements Action {
    readonly type: string;
    constructor(public readonly payload: CHDataActionPayload, public readonly callbackEmitter?: EventEmitter<CHDataAction<T>>) {}
}

export abstract class CHDataSuccessAction<T> implements Action {
    readonly type: string;
    constructor(
        public readonly result: CHSuccessActionResult<ClareHomeDbCollection<T>> | CHSuccessActionResult<T>,
        public readonly callbackEmitter?: EventEmitter<CHDataSuccessAction<T>>
    ) {}
}

export abstract class DataAction<T> implements Action {
    readonly type: string;
    constructor(public readonly payload: T, public readonly callbackEmitter?: EventEmitter<any>) {}
}

export abstract class DataSuccessAction<T> implements Action {
    readonly type: string;
    constructor(public readonly result: T, public readonly callbackEmitter?: EventEmitter<DataSuccessAction<T>>) {}
}

export abstract class DataErrorAction<T> implements Action {
    readonly type: string;
    constructor(public readonly error: DataServiceError<T>) {}
}

type SuccessActionConstructor<T> = new (data: T) => Action;
type ErrorActionConstructor<T> = new (error: DataServiceError<T>) => DataErrorAction<T>;
type ErrorActionObservable<T> = (error: DataServiceError<T>) => Observable<DataErrorAction<T>>;
type ErrorActionObservable2<T> = (error: DataServiceError<T>) => Observable<TypedAction<string>>;

export const toCustomErrorAction =
    <T>(errorAction: ErrorActionConstructor<T>, customErrorMsg: string) =>
    (error: any) => {
        const serviceError = new DataServiceError<T>(customErrorMsg, error);
        return toError(errorAction)(serviceError);
    };
/**
 *
 * @param errorAction
 * @deprecated
 */
export const toError =
    <T>(errorAction: ErrorActionConstructor<T>): ErrorActionObservable<T> =>
    (error: DataServiceError<T>) =>
        // TODO: dispatch a save_audit_event with the error
        of(new errorAction(error));

export const toError2 =
    <T>(errorAction: FailureAction<T>): ErrorActionObservable2<T> =>
    (error: DataServiceError<T>) =>
        // TODO: dispatch a save_audit_event with the error
        of(errorAction({ error }));

export const checkCurrentID = <T>(idSelector: Observable<string>, payloadID: string): UnaryFunction<unknown, Observable<T>> => {
    if (idSelector) {
        return pipe(
            withLatestFrom(idSelector),
            filter(([_, currentID]: [T, string]) => currentID === payloadID),
            map(([data]: [T, string]) => data)
        );
    }

    return pipe(map((_: T) => _));
};

/**
 *
 * @param {...any} actions
 * @deprecated
 */
export const toAction =
    (...actions: Action[]) =>
    <T>(
        source: Observable<T>,
        successAction: SuccessActionConstructor<T>,
        errorAction: ErrorActionConstructor<T>,
        callback?: EventEmitter<Action>
    ) =>
        source.pipe(
            mergeMap((data: T) => {
                const action = new successAction(data);

                if (callback) {
                    console.log(action);
                    // emit the success action and return any other actions
                    callback.emit(action);
                    return [...actions];
                }
                // return all actions
                return [action, ...actions];
            }),
            catchError(toError(errorAction))
        );

export const toAction2 =
    <T, T2 = T>(...actions: Action[]) =>
    (source: Observable<T>, successAction: SuccessAction<T>, errorAction: FailureAction<T2>, callback?: EventEmitter<Action>) =>
        source.pipe(
            mergeMap(result => {
                const action = successAction(result);

                if (callback) {
                    // emit the success action and return any other actions
                    callback.emit(action);
                    return [...actions];
                }
                // return all actions
                return [action, ...actions];
            }),
            catchError(e => toError2(errorAction)(e).pipe(tap(action => callback?.emit(action))))
        );

/**
 *
 * @param {...any} actions
 * @deprecated
 */
// Function of additional success actions
// that returns a function that returns
// an observable of ngrx action(s) from DataService method observable
export const toConfirmationDismissAction =
    (...actions: Action[]) =>
    <T>(
        source: Observable<T>,
        cancelAction: new () => Action,
        confirmAction: new (data: T) => Action,
        errorAction: ErrorActionConstructor<T>
    ) =>
        source.pipe(
            mergeMap((data: T) => (data ? [new confirmAction(data), ...actions] : [new cancelAction(), ...actions])),
            catchError(toError(errorAction))
        );

/**
 * A handler to display a positive confirmation SnackBar to the user after an action is taken
 *
 * @deprecated
 * @param actionWord Action word used to describe the action being taken on the entity type
 * @param navigatePath Path to navigate to after the snackbar is displayed
 * @param stayAlive
 * @param typeName The type of entity the action was taken on
 * @description Works with HttpResponseHandler to display a message to users in the form of:
 * "The [typeName] was successfully [actionWord]"
 * ex: "The device was successfully saved"
 * Will return the SnackBar action when done to be dispatched and display the message to the user
 */
export const handleSuccessMessage =
    <T extends DbObject | DbObject[] | any>(actionWord: ActionWord, navigatePath = "", typeName?: string, stayAlive = false) =>
    (action: DataAction<T>): Action => {
        let type = typeName;
        if (!type) {
            type = Array.isArray(action.payload)
                ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  (<typeof DbObject>action.payload[0].constructor).dbTypeName
                : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  (<typeof DbObject>action.payload.constructor).dbTypeName;
        }
        const message = new HttpResponseHandler(type, actionWord);
        return SnackbarActions.openSnackbar({
            message,
            path: navigatePath ? `/${navigatePath}` : null,
            stayAlive,
        });
    };

/**
 * A handler to display a "danger" SnackBar to the user after an action is taken
 *
 * @deprecated
 * @param actionWord Action word used to describe the action being taken on the entity type
 * @param typeName The type of entity the action was taken on
 * @description Works with HttpResponseHandler to display an error message to users in the form of:
 * "The [typeName] was not [actionWord]"
 * ex: "The device was not saved"
 * Will return the SnackBar action when done to be dispatched and display the message to the user
 */
export const handleErrorMessage =
    <T extends DbObject>(actionWord?: ActionWord, typeName?: string) =>
    (action: DataErrorAction<T>): Action => {
        let type = typeName || "item";
        if (action?.error?.requestData) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            type = (<typeof DbObject>action.error.requestData.constructor).dbTypeName;
        }
        const message = new HttpResponseHandler(type, actionWord, action.error.error, true);
        return SnackbarActions.openSnackbar({ message });
    };

/**
 * A handler to display a positive confirmation SnackBar to the user after an action is taken
 *
 * @param actionWord Action word used to describe the action being taken on the entity type
 * @param navigatePath Path to navigate to after the snackbar is displayed
 * @param typeName The type of entity the action was taken on
 * @description Works with HttpResponseHandler to display a message to users in the form of:
 * "The [typeName] was successfully [actionWord]"
 * ex: "The device was successfully saved"
 * Will return the SnackBar action when done to be dispatched and display the message to the user
 */
export const handleSuccessMessage2 =
    (actionWord: ActionWord, navigatePath = "", typeName: string = "") =>
    () => {
        const message = new HttpResponseHandler(typeName, actionWord);
        return SnackbarActions.openSnackbar({
            message,
            path: navigatePath ? `/${navigatePath}` : null,
        });
    };

/**
 * A handler to display a "danger" SnackBar to the user after an action is taken
 *
 * @param actionWord Action word used to describe the action being taken on the entity type
 * @param typeName The type of entity the action was taken on
 * @param customErrorMessage
 * @description Works with HttpResponseHandler to display an error message to users in the form of:
 * "The [typeName] was not [actionWord]"
 * ex: "The device was not saved"
 * Will return the SnackBar action when done to be dispatched and display the message to the user
 */
export const handleErrorMessage2 =
    <T extends DbObject>(actionWord?: ActionWord, typeName = "item", customErrorMessage?: string) =>
    (action: DataErrorAction<T>) => {
        const message = new HttpResponseHandler(typeName, actionWord, customErrorMessage || action.error.error, true);
        return SnackbarActions.openSnackbar({ message });
    };
